import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC, Key, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { useEnum } from "@/api/services/global/enum";
import { useTagOptions } from "@/api/services/main/tag";
import { Button } from "@/components/elements/Button";
import { JobTagEditorModal } from "@/features/job/components/job-tag-editor/JobTagEditorModal";
import dayjs from "dayjs";
import { black, orange, text } from "@/theme/colors";
import { IconInfo } from "@/assets/icons";
import { useParams } from "react-router-dom";
import { IconAlertWarning } from "@/assets/icons/IconAlertWarning";
import { JOB_STATUS, TJobStatus } from "@/types/enum";
import { useGetMatchInfo } from "@/api/services/main/match";
import { TOption } from "@/types/common";
import { useMe } from "@/api/services/main/auth";
import { useGetUserSettingInfo } from "@/api/services/main/user-setting";
import { truncateWithEllipsis } from "@/utils/string";

type TJobStatusProps = {
  isDraft?: boolean;
  originalStatus?: TJobStatus | null;
  userOptions: TOption[];
  isLoadingUserList: boolean;
  recruiterId?: number | null;
};

export const JobStatus: FC<TJobStatusProps> = ({ isDraft, originalStatus, userOptions, isLoadingUserList, recruiterId }) => {
  const { jobId } = useParams();
  const methods = useFormContext<TJobForm>();
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = methods;
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { data: jobTagOptions } = useTagOptions("job");
  const [isTagEditorOpen, setIsTagEditorOpen] = useState(false);
  const { data: matchInfoResponse } = useGetMatchInfo();
  const { data: meResult } = useMe();

  // Build match office options from match offices
  const matchOfficeOptions = useMemo(() => {
    const options: TOption[] = [];
    const matchOffices = matchInfoResponse?.data?.matchOffices;
    if (matchOffices && Array.isArray(matchOffices)) {
      const sortedOffices = [...matchOffices].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
      sortedOffices.forEach((office) => {
        if (office?.id && office?.officeName && office.officeName.trim() !== "") {
          options.push({ label: truncateWithEllipsis(office.officeName, 27), value: office.id });
        }
      });
    }
    return options;
  }, [matchInfoResponse]);

  // Fetch user setting for auto-selecting 紹介事業所名 default
  const { data: userSettingData } = useGetUserSettingInfo();

  useEffect(() => {
    if (recruiterId) return;

    const userData = meResult?.data;
    if (userData?.id && userData?.isMatchUser) {
      const parsedUserId = Number(userData?.id);
      if (!isNaN(parsedUserId)) {
        setValue("recruiterId", parsedUserId);
      }
    }
  }, [setValue, meResult, recruiterId]);

  // Auto-select matchOfficeId from user setting only when creating new job
  useEffect(() => {
    if (jobId) return;

    const settingMatchOfficeId = userSettingData?.data?.matchOfficeId;
    if (settingMatchOfficeId && matchOfficeOptions.some((opt) => opt.value === settingMatchOfficeId)) {
      setValue("matchOfficeId", settingMatchOfficeId);
    }
  }, [setValue, userSettingData, jobId, matchOfficeOptions]);

  const currentStatus = useWatch({ control, name: "status" });
  const isOriginalStatusPausedOrClosed = originalStatus === JOB_STATUS.PAUSED || originalStatus === JOB_STATUS.CLOSED;
  const isStatusChanged = currentStatus !== originalStatus && ![JOB_STATUS.PAUSED, JOB_STATUS.CLOSED].includes(currentStatus);
  const shouldShowReactivationWarning = jobId && isOriginalStatusPausedOrClosed && isStatusChanged;

  const tagIdsWatchField = useWatch({ control, name: "tagIds" });

  const startDateWatchField = useWatch({
    control,
    name: "startDate",
  });

  const endDateWatchField = useWatch({
    control,
    name: "endDate",
  });

  // When startDate changes and endDate is currently in an error state, trigger re-validate endDate
  useEffect(() => {
    if (errors?.endDate) {
      trigger("endDate");
    }
  }, [startDateWatchField]);

  // When endDate changes and startDate is currently in an error state, trigger re-validate startDate
  useEffect(() => {
    if (errors?.startDate) {
      trigger("startDate");
    }
  }, [endDateWatchField]);

  // shouldDirty: programmatic setValue must notify RHF so the form's watch()-based change detection
  // (JobForm) marks the form dirty → unsaved-changes modal shows.
  const handleTagIdsChange = (value: Key[]) => {
    setValue("tagIds", value.length > 0 ? value.map(String) : null, { shouldDirty: true });
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="ステータス状況" />} showDivider={false}>
        {!jobId && (
          <Stack direction="row" alignItems="center" spacing={1} my={2}>
            <IconInfo color={black[80]} fontSize={16} />
            <Typography variant="cap12" color={text.secondary}>
              ステータスを「下書き」にすれば、企業名、求人名、求人担当者を入力するだけで保存できます。
            </Typography>
          </Stack>
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="status">
              <Select placeholder="選択してください" options={jobStatusOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <Stack direction="row" alignItems="center">
              <FormField control={control} name="tagIds">
                {/* Tag ids are strings (tags.id is bigint). CheckboxGroup works with string Keys directly. */}
                <CheckboxGroup
                  nonControl
                  value={tagIdsWatchField ?? []}
                  flexDirection="row"
                  rowGap={1}
                  columnGap={3}
                  options={jobTagOptions.map((opt) => ({ ...opt, value: String(opt.value) }))}
                  onChange={handleTagIdsChange}
                />
                <Typography variant="cap12" color={text.secondary}>
                  新着求人は2週間「NEW」のタグが付与されます。
                </Typography>
              </FormField>
              <Button variant="ghost" size="sm" onClick={() => setIsTagEditorOpen(true)}>
                編集
              </Button>
            </Stack>
          </Grid>
          {shouldShowReactivationWarning && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <IconAlertWarning color={orange[80]} width={16} height={16} />
                <Typography variant="cap12" color={orange[80]}>
                  ステータスを募集中に戻しても今月の求人としてカウントされません。 再募集の場合は、ページを戻ってから複製をご利用ください。
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={6}>
            <FormField control={control} name="startDate">
              <InputDatePicker minDate={dayjs("1900/01/01")} maxDate={dayjs("2099/12/31")} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="endDate">
              <InputDatePicker minDate={dayjs("1900/01/01")} maxDate={dayjs("2099/12/31")} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="matchOfficeId" required={!isDraft}>
              <Select placeholder="選択してください" options={matchOfficeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="recruiterId">
              <Select placeholder="選択してください" options={userOptions} loading={isLoadingUserList} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="statusMemo">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>
      <JobTagEditorModal isOpen={isTagEditorOpen} onClose={() => setIsTagEditorOpen(false)} />
    </Stack>
  );
};
